import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import {
  getProductDefaultInfo,
  getCartProductInfo,
} from '../../../../utilities/selectors';
import {
  getZemplinProductDefaultInfo,
  getZemplinCartProductInfo,
  getZemplinTrueCount,
} from '../../selectors';
import { OrderNrInput } from '../../Product/OrderNrInput';
import { StockInfo } from '../../Product/StockInfo';
import { ProductPriceCartTable } from '../../Product/ProductPriceCartTable';
import { TableTd, TableRow } from '../../General/Table';
import {
  FlexCol,
  FlexRowCenterHorizontal,
} from '../../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../../Defaults/Text';
import { IconType, SvgIcon } from '../../General/SvgIcon';
import { CountSetter } from '../../ProductDetail/CountSetter';
import { FormInput } from '../../Form/FormInput';
import { DotDelimeter } from '../../General/DotDelimeter';
import { CustomNrStock } from './CustomNrStock';

export interface Props {
  product: any;
  addToRequest: any;
  theme: any;
  removeFromCart: any;
  changeCartItemNote: (gId: number, note: string) => void;
}

function RequestTabRow({
  product,
  addToRequest,
  theme,
  removeFromCart,
  changeCartItemNote,
}: Props) {
  const {
    picture,
    url,
    name,
    ordernr: currentOrdernr,
    productId,
    goodId,
    productNote,
  } = getCartProductInfo(product);

  const {
    unit,
    count: initialCount,
    productPackages,
    onStockExternal,
    wholePackagesOnly,
    defaultPackageSize,
    onStock,
  } = getZemplinCartProductInfo(product);

  const [count, setCount] = React.useState(initialCount);
  const [note, setNote] = React.useState(productNote || '');
  const isMks = unit === 'Mks';
  const step = productPackages ? productPackages : isMks ? 0.001 : 1;
  const minAmount = unit === 'Mks' ? 0.001 : 1;
  const stockSum = onStock + onStockExternal;
  const handleNoteChange = e => {
    const val = e.target.value;
    setNote(val);
  };

  const handleBlur = () => {
    changeCartItemNote(goodId, note);
  };
  const trueOnStock = isMks ? stockSum / 1000 : stockSum;
  const handleCountChange = (
    newCount: string,
    shouldAddToCart: boolean = true,
  ) => {
    const withoutCommas = newCount.toString().replace(',', '.');
    let newCountNumber = parseFloat(withoutCommas);

    if (isNaN(newCountNumber)) {
      setCount(newCount);
      return;
    }

    setCount(newCount);
  };

  const handleRemoveClick = () => {
    removeFromCart(productId, goodId, true, product);
  };

  return (
    <>
      <ImageNameTableTd>
        <ImageWrapper to={url}>
          <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
        </ImageWrapper>
        <NameInputWrapper>
          <NameLink to={url}>
            <Name>{name}</Name>
          </NameLink>
          <CustomNrStock
            customNr={currentOrdernr}
            stockReadable={''}
            unit={unit}
            availText={''}
            storesInfo={null}
          />
        </NameInputWrapper>
      </ImageNameTableTd>
      <StyledTableTd>
        <CountWrapper>
          <CountSetter
            step={step}
            // tslint:disable-next-line:jsx-no-lambda
            onChange={handleCountChange}
            unit={unit}
            count={count}
            defaultPackageSize={defaultPackageSize}
            wholePackagesOnly={wholePackagesOnly}
            onStock={trueOnStock}
          />
        </CountWrapper>
      </StyledTableTd>
      <StyledTableTd>
        <FormInput
          name={'request-pr-note'}
          type="text"
          value={note}
          onChange={handleNoteChange}
          onBlur={handleBlur}
          marginRight={0}
          marginBottom={0}
          maxWidthPx={320}
        />
      </StyledTableTd>
      <StyledTableTd>
        <SvgIcon
          onClick={handleRemoveClick}
          width={16}
          height={16}
          icon={IconType.trash}
          marginLeft={16}
          viewBox="0 0 16 16"
          alt="Odstrániť produkt"
          title="Odstrániť produkt"
          fill={theme.colors.textPrimary}
          cursor={'pointer'}
        />
      </StyledTableTd>
    </>
  );
}

const StyledTableTd = styled(TableTd)`
  padding: ${rem(16)} 0;
  margin-left: ${rem(8)};
  border-bottom: ${({ theme }) => theme.borders.primary};
`;

const CountWrapper = styled(FlexRowCenterHorizontal)`
  margin-left: ${rem(28)};
`;

const ImageNameTableTd = styled(StyledTableTd)`
  display: flex;
  align-items: center;
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(16)};
`;

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
  max-width: initial;
`;

const NameInputWrapper = styled(FlexCol)``;

const NameLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    text-decoration: underline;
  }
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(4)};

  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondaryLight};
  font-weight: 400;
  white-space: pre-line;
  max-width: ${rem(400)};
  max-height: ${rem(100)};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.l1300`
      max-width: ${rem(400)};
  `}

  ${({ theme }) => theme.mediab.l1150`
      max-width: ${rem(300)};
  `}
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const WithTheme: any = withTheme(RequestTabRow);
export { WithTheme as RequestTabRow, StyledTableRow };
